import React from 'react';
import { PAYROLL_MODULE } from './payroll/catalog';
import { ADMIN_VIEW_PERMS, PERM, canSeeEntry } from './utils';

// ─────────────────────────────────────────────
// NAV STRUCTURE  (ids / roles / badges preserved)
// ─────────────────────────────────────────────
export const NAV = [
  {
    group: 'OVERVIEW',
    items: [
      // No `roles` key means open to every signed-in user. hasRole() also
      // grants super_admin everything, so ['super_admin'] reads as
      // "super admin only" — used below for the modules being held back.
      { id: 'dashboard', label: 'Dashboard', icon: '⬡', requires: PERM.DASHBOARD_VIEW },
      // `comingSoon` modules are placeholders: their card and sidebar entry open
      // the Coming Soon page until the module is built.
      { id: 'command-center', label: 'Command Center', description: 'Company-wide overview, requests, approvals and alerts.', roles: ['super_admin'], comingSoon: true },
      // Parent modules (with `children`): the card and sidebar entry open a hub
      // listing the children, and show for anyone who can see at least one child
      // (and who has the parent's own `roles`, when it sets any).
      // Open to every role: Inventory & Procurement, Enterprise Requisition and My Profile;
      // everything else on the dashboard is super admin only unless it lists its own roles.
      {
        id: 'cost-control', label: 'Cost Control', description: 'Report entry, bills of quantities and QS site installation.', icon: '◔', roles: ['super_admin'], perms: [PERM.COST_CONTROL_VIEW, PERM.BOQ_HUB_VIEW, PERM.QS_HUB_VIEW],
        children: [
          { id: 'report-entry', label: 'Report Entry', description: 'Submit DMR, QS installation and BOQ entry forms.', icon: '📝', roles: ['super_admin'], perms: [PERM.COST_CONTROL_VIEW] },
          { id: 'boq-hub', label: 'BOQ Hub', description: 'Bills of quantities, rates and project budgets.', icon: '📊', roles: ['super_admin'], perms: [PERM.BOQ_HUB_VIEW] },
          { id: 'qsi-hub', label: 'QS Hub', description: 'Quantity surveying and site installation records.', icon: '🏗️', roles: ['super_admin'], perms: [PERM.QS_HUB_VIEW] },
        ],
      },
      // `links`: the module's own pages, shown as cards on its page and as its sidebar submenu.
      // The All-In-One Expense Register is open to everyone; the older registers keep their roles.
      {
        id: 'expense-master', label: 'Expense Master', description: 'Expense registers and the all-in-one voucher register.', icon: '🧾', roles: ['finance_head', 'accounts_officer'], perms: [PERM.EXPENSE_MASTER_VIEW],
        links: [
          { id: 'dmr', label: 'Direct Material Register', description: 'Record and track direct material expenses.', icon: '📦', lucide: 'package', to: '/expense-master/dmr', roles: ['super_admin', 'project_manager'], perms: [PERM.EXPENSE_MASTER_VIEW] },
          { id: 'other', label: 'Other Expense Register', description: 'Record and track miscellaneous and other expenses.', icon: '🧾', lucide: 'receipt', to: '/expense-master/coming-soon?module=Other Expense Register', roles: ['super_admin', 'project_manager'] },
          { id: 'expense-register', label: 'All-In-One Expense Register', description: 'Every posted and approved payment voucher line, with its requisition details.', icon: '📊', lucide: 'sheet', to: '/expense-master/expense-register' },
        ],
      },
      // Hidden from the dashboard and sidebar for now; /projects routes still work.
      // { id: 'projects', label: 'Projects', description: 'Project setup, execution, labour and finance.', icon: '◈', roles: ['super_admin'] },
      {
        id: 'inventory', label: 'Inventory & Procurement', description: 'Material requests, purchase orders, stock and stores.', icon: '📦',
        children: [
          { id: 'material-takeoff', label: 'Material Take-Off', description: 'Engineering take-off: break BOQ lines into materials, approve, then raise material requests.', icon: '📐' },
          { id: 'material-request', label: 'Material Request', description: 'Request materials for projects and sites.', icon: '🏗', requires: PERM.MATERIAL_REQUEST_VIEW },
          { id: 'material-purchase-orders', label: 'Material PO', description: 'Raise and manage material purchase orders.', icon: '🛒', requires: PERM.MATERIAL_PO_VIEW },
        ],
      },
      { id: 'enterprise-requisition', label: 'Enterprise Requisition', description: 'Raise, track and approve requisitions.', icon: '🛒', requires: PERM.ENTERPRISE_REQUISITION_VIEW },
      // The approval queues (Approve Posted Vouchers, Approval Queue) live
      // inside this module, so the approver roles need the entry too —
      // otherwise a Finance Head or MD/DMD has no way to reach their queue.
      { id: 'payment-voucher-engine', label: 'Payment Voucher', description: 'Prepare, post and approve payment vouchers.', icon: '💳', roles: ['payment_voucher', 'finance_head', 'md', 'dmd'], perms: [PERM.PAYMENT_VOUCHER_VIEW] },
      // HR & People keeps the Stephanet navigation: the sidebar entry expands into its submenus
      // (src/hr/catalog.js) and opens on the Employee Command Center.
      { id: 'hr-people', label: 'HR & People', description: 'Employee records, recruitment, attendance, leave and welfare.', roles: ['super_admin'], perms: [PERM.HR_PEOPLE_VIEW], icon: '👥', submenu: 'hr' },
      { id: 'payroll', label: 'Payroll & Compensation', description: 'Payroll engine, pay structure, statutory deductions and payslips.', roles: ['super_admin'], perms: [PERM.PAYROLL_VIEW, PERM.PAYROLL_INPUTS, PERM.PAYROLL_LOANS, PERM.PAYROLL_RUNS, PERM.PAYROLL_COMPENSATION, PERM.PAYROLL_PAYMENTS], icon: '💰',
        // Stephanet's flow: the sidebar entry expands into the payroll groups (src/payroll/catalog.js)
        links: PAYROLL_MODULE.groups.map(g => ({ id: `payroll-${g.key}`, label: g.title, to: `/payroll/s/${g.submodules[0].key}`, lucide: g.icon,
                                                 roles: ['super_admin'], perms: [PERM.PAYROLL_VIEW, PERM.PAYROLL_INPUTS, PERM.PAYROLL_LOANS, PERM.PAYROLL_RUNS, PERM.PAYROLL_COMPENSATION, PERM.PAYROLL_PAYMENTS] })) },
      { id: 'loans-recoveries', label: 'Loans & Recoveries', description: 'Loan and advance management from application to full recovery.', roles: ['super_admin'], comingSoon: true },
      { id: 'tools-assets', label: 'Tools & Asset Management', description: 'Tools control, company assets, PPE, maintenance and IT lifecycle.', roles: ['super_admin'], comingSoon: true },
      { id: 'fleet-compliance', label: 'Fleet & Compliance', description: 'Vehicles, drivers, fuel, maintenance, downtime and licences.', roles: ['super_admin'], comingSoon: true },
      { id: 'cost-reports', label: 'Cost Reports', description: 'Cost to company, projects, BOQ, budgets and P&L.', roles: ['super_admin'], comingSoon: true },
    ],
  },
  {
    group: 'ADMIN',
    items: [
      // `perms`: also shown to anyone granted one of these in the Permission Matrix.
      // `requires` (Dashboard, Enterprise Requisition, Material Request, Material PO): hidden from
      // anyone without that view permission, whatever their roles.
      { id: 'admin', label: 'System Administration', description: 'Users, roles, access control and system setup.', icon: '⊞', roles: ['super_admin'], perms: ADMIN_VIEW_PERMS },
      { id: 'profile', label: 'My Profile', description: 'Your account details and password.', icon: '◎' },
    ],
  },
];

// Visible when the user has the item's roles; a parent also needs at least one visible child.
// Placeholder modules, each routed to the Coming Soon page.
// Settings is no longer a module here: it is a card inside System Administration (pages/AdminPage.js);
// the old /settings address redirects there (App.js).
export const COMING_SOON_MODULES = NAV.flatMap(g => g.items).filter(item => item.comingSoon);

export const canSeeNavItem = (user, item) =>
  canSeeEntry(user, item) && (!item.children || item.children.some(c => canSeeEntry(user, c)));

// A module's sidebar submenu: its children (hub modules) or its own `links`, those the user may open.
// HR & People builds its submenu from src/hr/catalog.js instead.
export const navSubmenu = (user, item) => {
  if (item.children) return item.children.filter(c => canSeeEntry(user, c)).map(c => ({ id: c.id, label: c.label, to: `/${c.id}` }));
  if (item.links) return item.links.filter(l => canSeeEntry(user, l)).map(l => ({ id: l.id, label: l.label, to: l.to, lucide: l.lucide }));
  return [];
};

// Routes that belong to a parent module, so the parent stays highlighted inside them.
export const navItemPaths = item =>
  [item.id, ...(item.children || []).map(c => c.id)];

// ─── module icons ────────────────────────────
// One line icon per module. Cards draw it in its own colour on a soft tile;
// the sidebar draws the same shape in the rail's text colour.
const ci = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' };
export const MODULE_ICONS = {
  'command-center': {
    color: '#0891b2',
    svg: (<svg {...ci}><path d="m12 14 4-4" /><path d="M3.34 19a10 10 0 1 1 17.32 0" /></svg>),
  },
  'hr-people': {
    color: '#0b318bff',
    svg: (<svg {...ci}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>),
  },
  'payroll': {
    color: '#2563eb',
    svg: (<svg {...ci}><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2" /><path d="M6 12h.01" /><path d="M18 12h.01" /></svg>),
  },
  'loans-recoveries': {
    color: '#d97706',
    svg: (<svg {...ci}><path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17" /><path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" /><path d="m2 16 6 6" /><circle cx="16" cy="9" r="2.9" /><circle cx="6" cy="5" r="3" /></svg>),
  },
  'tools-assets': {
    color: '#06b6d4',
    svg: (<svg {...ci}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>),
  },
  'fleet-compliance': {
    color: '#1d4ed8',
    svg: (<svg {...ci}><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" /></svg>),
  },
  'cost-reports': {
    color: '#ca8a04',
    svg: (<svg {...ci}><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M8 18v-2" /><path d="M12 18v-4" /><path d="M16 18v-6" /></svg>),
  },
  'settings': {
    color: '#0f766e',
    svg: (<svg {...ci}><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></svg>),
  },
  'cost-control': {
    color: '#e11d48',
    svg: (<svg {...ci}><path d="M21 12c.55 0 1-.45.95-1A10 10 0 0 0 13 2.05c-.55-.05-1 .4-1 .95v8a1 1 0 0 0 1 1z" /><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /></svg>),
  },
  'report-entry': {
    color: '#2563eb',
    svg: (<svg {...ci}><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="M8 12h8" /><path d="M8 16h5" /></svg>),
  },
  'expense-master': {
    color: '#059669',
    svg: (<svg {...ci}><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" /><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" /><path d="M12 17.5v-11" /></svg>),
  },
  'boq-hub': {
    color: '#7c3aed',
    svg: (<svg {...ci}><rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="16" y1="14" x2="16" y2="18" /><path d="M16 10h.01" /><path d="M12 10h.01" /><path d="M8 10h.01" /><path d="M12 14h.01" /><path d="M8 14h.01" /><path d="M12 18h.01" /><path d="M8 18h.01" /></svg>),
  },
  // BOQ Hub's own cards (pages/BOQHubPage.js)
  'budget-master': {
    color: '#7c3aed',
    svg: (<svg {...ci}><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="5" width="3" height="13" /></svg>),
  },
  'material-mappings': {
    color: '#0891b2',
    svg: (<svg {...ci}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><path d="M10 6.5h4a3 3 0 0 1 3 3V14" /><path d="m15 12 2 2 2-2" /></svg>),
  },
  'code-generator': {
    color: '#db2777',
    svg: (<svg {...ci}><path d="M4 9h16" /><path d="M4 15h16" /><path d="M10 3 8 21" /><path d="M16 3l-2 18" /></svg>),
  },
  'qsi-hub': {
    color: '#d97706',
    svg: (<svg {...ci}><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z" /><path d="m14.5 12.5 2-2" /><path d="m11.5 9.5 2-2" /><path d="m8.5 6.5 2-2" /><path d="m17.5 15.5 2-2" /></svg>),
  },
  projects: {
    color: '#0891b2',
    svg: (<svg {...ci}><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" /><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" /><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" /><path d="M10 6h4" /><path d="M10 10h4" /><path d="M10 14h4" /><path d="M10 18h4" /></svg>),
  },
  inventory: {
    color: '#16a34a',
    svg: (<svg {...ci}><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" /><path d="M12 22V12" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="m7.5 4.27 9 5.15" /></svg>),
  },
  'enterprise-requisition': {
    color: '#ea580c',
    svg: (<svg {...ci}><circle cx="8" cy="21" r="1" /><circle cx="19" cy="21" r="1" /><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" /></svg>),
  },
  'payment-voucher-engine': {
    color: '#4f46e5',
    svg: (<svg {...ci}><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" /></svg>),
  },
  'material-takeoff': {
    color: '#0369a1',
    svg: (<svg {...ci}><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="M12 11h4" /><path d="M12 16h4" /><path d="M8 11h.01" /><path d="M8 16h.01" /></svg>),
  },
  'material-request': {
    color: '#0d9488',
    svg: (<svg {...ci}><path d="M16 16h6" /><path d="M19 13v6" /><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14" /><path d="m7.5 4.27 9 5.15" /><path d="M3.29 7 12 12l8.71-5" /><path d="M12 22V12" /></svg>),
  },
  'material-purchase-orders': {
    color: '#db2777',
    svg: (<svg {...ci}><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" /></svg>),
  },
  admin: {
    color: '#0284c7',
    svg: (<svg {...ci}><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></svg>),
  },
  profile: {
    color: '#9333ea',
    svg: (<svg {...ci}><circle cx="12" cy="8" r="5" /><path d="M20 21a8 8 0 0 0-16 0" /></svg>),
  },
};

// ─── sidebar icons by nav id ─────────────────
const ic = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' };
export const NAV_ICONS = {
  dashboard: (<svg {...ic}><rect x="3" y="3" width="7" height="9" rx="1.3" /><rect x="14" y="3" width="7" height="5" rx="1.3" /><rect x="14" y="12" width="7" height="9" rx="1.3" /><rect x="3" y="16" width="7" height="5" rx="1.3" /></svg>),
  alerts: (<svg {...ic}><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /></svg>),
  ...Object.fromEntries(Object.entries(MODULE_ICONS).map(([id, m]) => [id, React.cloneElement(m.svg, { width: 18, height: 18 })])),
};
